import React, {
  ChangeEvent,
  useState,
  useCallback,
  useRef,
  useEffect,
} from "react";

import { Box, TextField, useTheme } from "@mui/material";

interface Props {
  name: string;
  values: any;
  onUpdateText: (text: string) => void;
  isCurrentParagraph: boolean;
  onParagraphClick: () => void;
}

const InputField = React.forwardRef<HTMLDivElement, Props>(
  (
    { name, values, onUpdateText, isCurrentParagraph, onParagraphClick },
    ref,
  ) => {
    const [localValue, setLocalValue] = useState<string>(values[name] || "");
    const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const theme = useTheme();

    // Update local value when props change
    useEffect(() => {
      setLocalValue(values[name] || "");
    }, [values, name]);

    // Optimized debounced update function
    const debouncedUpdateText = useCallback(
      (text: string) => {
        if (debounceTimeoutRef.current) {
          clearTimeout(debounceTimeoutRef.current);
        }

        debounceTimeoutRef.current = setTimeout(() => {
          // Use requestAnimationFrame to defer the update and prevent ResizeObserver loops
          requestAnimationFrame(() => {
            onUpdateText(text);
          });
        }, 500);
      },
      [onUpdateText],
    );

    // Memoized change handler
    const handleChange = useCallback(
      (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const newValue = e.target.value;
        setLocalValue(newValue);
        debouncedUpdateText(newValue);
      },
      [debouncedUpdateText],
    );

    // Memoized click handler
    const handleClick = useCallback(() => {
      // Defer the click handler to prevent ResizeObserver loops
      requestAnimationFrame(() => {
        onParagraphClick();
      });
    }, [onParagraphClick]);

    // Cleanup timeout on unmount
    useEffect(() => {
      return () => {
        if (debounceTimeoutRef.current) {
          clearTimeout(debounceTimeoutRef.current);
        }
      };
    }, []);

    return (
      <Box
        ref={ref}
        sx={{
          mb: 0.5,
          p: 0.5,
          borderRadius: isCurrentParagraph ? "4px" : "0px",
          backgroundColor: isCurrentParagraph
            ? "rgba(99, 102, 241, 0.1)"
            : "transparent",
          border: isCurrentParagraph
            ? "1px solid rgba(99, 102, 241, 0.3)"
            : "none",
          transition: "all 0.2s ease",
          "&:hover": {
            backgroundColor: isCurrentParagraph
              ? "rgba(99, 102, 241, 0.15)"
              : "rgba(0, 0, 0, 0.05)",
            boxShadow: isCurrentParagraph
              ? "none"
              : "inset 2px 0 0 rgba(99, 102, 241, 0.3)",
          },
        }}
      >
        <TextField
          margin="dense"
          variant="standard"
          sx={{
            cursor: "pointer",
            backgroundColor: "transparent",
            "& .MuiInputBase-root": {
              minHeight: "auto",
              color: isCurrentParagraph
                ? theme.palette.text.primary
                : theme.palette.text.primary,
              fontSize: "14px",
              lineHeight: "1.4",
              padding: 0,
            },
            "& .MuiInputBase-input": {
              overflow: "hidden",
              textOverflow: "ellipsis",
              fontSize: "14px",
              lineHeight: "1.4",
              padding: 0,
              minHeight: "1.4em",
              wordWrap: "break-word",
              overflowWrap: "break-word",
              whiteSpace: "pre-wrap",
            },
            "& .MuiInput-underline:before": {
              borderBottom: "none",
            },
            "& .MuiInput-underline:after": {
              borderBottom: "none",
            },
            "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
              borderBottom: "none",
            },
          }}
          multiline
          fullWidth
          name={name}
          value={localValue}
          onChange={handleChange}
          onClick={handleClick}
          // Prevent unnecessary re-renders
          InputProps={{
            disableUnderline: true,
          }}
        />
      </Box>
    );
  },
);

InputField.displayName = "InputField";

export default InputField;
